/**
 * Short, distinguishing column labels for the Form Assignments grid.
 *
 * Visit names such as "Appointment 1. Scrutiny" share a long prefix, so a
 * narrow column that truncates the name shows every visit as "Appointm...".
 * These helpers are pure so they can be tested without React.
 */

/** Remove a trailing or inline "(...)" note such as "(Day 15)". */
function stripParentheses(name: string): string {
  const out = name.replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();
  return out || name;
}

/** The remainder is only worth showing when it carries a real word. */
function isMeaningful(rest: string): boolean {
  return /[A-Za-z]{3,}/.test(rest);
}

/**
 * Whether a leading word may be dropped in front of this remainder. A remainder
 * that starts with a bare number ("1 Day 15" from "Cycle 1 Day 15") is only half
 * a name, so the word before it is kept; a numbered ordinal with a delimiter
 * ("1. Scrutiny" from "Appointment 1. Scrutiny") stands on its own.
 */
function canDropLeadingWord(rest: string): boolean {
  if (!isMeaningful(rest)) return false;
  return !/^\d/.test(rest) || /^\d+[.):-]\s*\S/.test(rest);
}

/** Longest prefix shared by every name, cut back to a whole word plus its space. */
function sharedWordPrefix(names: string[]): string {
  if (names.length < 2) return '';
  let p = names[0];
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < p.length && i < n.length && p[i].toLowerCase() === n[i].toLowerCase()) i++;
    p = p.slice(0, i);
    if (!p) return '';
  }
  const cut = p.lastIndexOf(' ');
  return cut > 0 ? p.slice(0, cut + 1) : '';
}

/**
 * One short label per visit, in the order given. Labels are unique and never
 * empty. When shortening would make two labels equal, the affected visits fall
 * back to a longer form of the name, and as a last resort get an ordinal.
 */
export function visitColumnLabels(names: ReadonlyArray<string | null | undefined>): string[] {
  const original = names.map((n, i) => {
    const t = typeof n === 'string' ? n.replace(/\s+/g, ' ').trim() : '';
    return t || `Visit ${i + 1}`;
  });
  const cleaned = original.map(stripParentheses);

  const prefix = sharedWordPrefix(cleaned);
  const short = cleaned.map((c) => {
    if (prefix && c.length > prefix.length) {
      const rest = c.slice(prefix.length).trim();
      if (canDropLeadingWord(rest)) return rest;
    }
    // "Appointment 1. Scrutiny" -> "1. Scrutiny"
    const m = /^[A-Za-z]+\s+(\d+[.):-]\s*\S.*)$/.exec(c);
    if (m && canDropLeadingWord(m[1])) return m[1].trim();
    return c;
  });

  // Escalate colliding labels: short -> cleaned -> original.
  const tiers = [short, cleaned, original];
  const result = short.slice();
  for (let t = 1; t < tiers.length; t++) {
    const counts = new Map<string, number>();
    result.forEach((l) => counts.set(l, (counts.get(l) ?? 0) + 1));
    result.forEach((l, i) => {
      if ((counts.get(l) ?? 0) > 1) result[i] = tiers[t][i];
    });
  }

  // Identical full names: number the repeats so no two columns read the same.
  const seen = new Map<string, number>();
  const total = new Map<string, number>();
  result.forEach((l) => total.set(l, (total.get(l) ?? 0) + 1));
  return result.map((l) => {
    if ((total.get(l) ?? 0) < 2) return l;
    const k = (seen.get(l) ?? 0) + 1;
    seen.set(l, k);
    return `${l} (${k})`;
  });
}

/** "Day N" when the study day is known, otherwise nothing. */
export function formatVisitDay(day: number | null | undefined): string {
  return typeof day === 'number' && Number.isFinite(day) ? `Day ${day}` : '';
}

/** A compact visit window: "±3 d" when equal, "-2/+5 d" when not, '' when none. */
export function formatVisitWindow(before: number | null | undefined, after: number | null | undefined): string {
  const b = typeof before === 'number' && Number.isFinite(before) && before > 0 ? before : 0;
  const a = typeof after === 'number' && Number.isFinite(after) && after > 0 ? after : 0;
  if (!b && !a) return '';
  if (b === a) return `±${b} d`;
  return `-${b}/+${a} d`;
}
